import {
  computed,
  nextTick,
  onMounted,
  ref,
  toRef,
  watch,
} from 'vue';
import { useFlowRuleName, useI18n } from '@/composables';
import { useFlowNavigationErrors } from '@/composables/useFlowNavigationErrors';
import { MoveDirectionEnum } from '@/services/models/Enums';

type FlowNavigationRule = {
  default?: boolean;
  name?: string;
  status?: string;
  conditions?: Record<string, unknown>;
  paths: unknown[];
  active?: boolean;
};

type FlowNavigationForm = {
  name?: string;
  rules: FlowNavigationRule[];
};

type FlowNavigationProps = {
  form: FlowNavigationForm;
  countryName?: string;
  header: string;
  activeRule: FlowNavigationRule;
  formErrors: { rules: unknown[] };
  errorsRaw: unknown[][] | null;
  errorConditionPrefix?: string[];
};

const canMoveUp = (index: number) => index > 1;

const canMoveDown = (index: number, items: unknown[]) => index < items.length - 1;

const getSwapIndexes = (
  index: number,
  items: unknown[],
  direction: MoveDirectionEnum = MoveDirectionEnum.up,
) => {
  if (direction === MoveDirectionEnum.up) {
    if (!canMoveUp(index)) return;
    return { a: index - 1, b: index };
  }

  if (!canMoveDown(index, items)) return;
  return { a: index, b: index + 1 };
};

const swap = (items: unknown[], ids: { a: number; b: number }) => {
  const temp = items[ids.a];
  items[ids.a] = items[ids.b];
  items[ids.b] = temp;
};

type FlowNavigationEmits = {
  (event: 'update:view', value: string): void;
  (event: 'set-placeholder', value: string): void;
  (event: 'add-path', rule: FlowNavigationRule, callback: (item: unknown, rule: FlowNavigationRule, errors?: unknown) => void): void;
  (event: 'add-rule'): void;
  (event: 'set-rule', rule: FlowNavigationRule): void;
  (event: 'set-path', path: unknown): void;
  (event: 'set-errors', errors: unknown): void;
};

export const useFlowNavigation = (
  props: FlowNavigationProps,
  emit: FlowNavigationEmits,
) => {
  const { t } = useI18n();
  const { buildFlowRuleName } = useFlowRuleName();

  const errorConditionPrefix = computed(() => props.errorConditionPrefix || []);

  const { getRulesErrors, isGeneralError } = useFlowNavigationErrors(
    toRef(props, 'errorsRaw'),
    errorConditionPrefix,
  );

  const activeItem = ref<unknown>(null);
  const navigationErrors = ref<ReturnType<typeof getRulesErrors>>([]);
  const navigationHidden = ref(false);
  const openedGroups = ref<Array<string | number>>([0]);

  const nameDescription = computed(() => {
    const countryLabel = props.countryName || t('Global');
    const nameLabel = props.form.name || t('None');
    return `${countryLabel} - ${nameLabel}`;
  });

  const navigationErrorsRulesIndex = computed(() =>
    navigationErrors.value.map((item) => ({
      hasError: item.hasError,
      hasPathError: item.paths.some((path) => path.hasError),
    })),
  );

  const navigationErrorsPathsIndex = computed(() => {
    const result: Record<string, boolean> = {};

    navigationErrors.value
      .map((rule, ruleIndex) =>
        rule.paths.map((path, pathIndex) => ({
          ruleIndex,
          pathIndex,
          hasError: path.hasError,
        })),
      )
      .reduce((left, right) => left.concat(right), [])
      .forEach((item) => {
        result[getNavigationIndexKey(item.ruleIndex, item.pathIndex)] = item.hasError;
      });

    return result;
  });

  const getNavigationIndexKey = (ruleIndex: number, pathIndex: number) => `${ruleIndex}_${pathIndex}`;

  const moveRule = (
    index: number,
    items: unknown[],
    direction: MoveDirectionEnum = MoveDirectionEnum.up,
  ) => {
    const ids = getSwapIndexes(index, items, direction);
    if (ids) {
      swap(items, ids);
    }
  };

  const handleMoveRule = (index: number, direction: MoveDirectionEnum) => {
    moveRule(index, props.form.rules, direction);
  };

  const toggleGroup = (ruleIndex: number) => {
    const isOpen = openedGroups.value.includes(ruleIndex);
    openedGroups.value = isOpen
      ? openedGroups.value.filter((value) => value !== ruleIndex)
      : [...openedGroups.value, ruleIndex];
  };

  const openNavigation = () => {
    openedGroups.value = props.form.rules.map((_, ruleIndex) => ruleIndex);
  };

  const selectFlowSettings = () => {
    emit('update:view', 'settings');
    activeItem.value = null;
  };

  const setPath = (item: unknown, rule: FlowNavigationRule, errors: unknown = undefined) => {
    nextTick(() => {
      emit('update:view', 'path-settings');
      activeItem.value = item;
      emit('set-rule', rule);
      emit('set-path', item);
      emit('set-errors', errors);
    });
  };

  const setRule = (item: FlowNavigationRule, errors: unknown = undefined) => {
    if (!item.default) {
      emit('update:view', 'rule-settings');
      activeItem.value = item;
      emit('set-rule', item);
      emit('set-errors', errors);
    } else {
      setPath(item.paths[0], item);
    }
  };

  watch(
    () => props.activeRule,
    (value) => {
      emit('set-placeholder', String(buildFlowRuleName(value)));
    },
    { deep: true },
  );

  watch(
    () => props.form.rules.length,
    (length) => {
      // Keep newly added rules expanded
      const nextOpened = new Set(openedGroups.value);
      for (let ruleIndex = 0; ruleIndex < length; ruleIndex++) {
        if (ruleIndex === length - 1) {
          nextOpened.add(ruleIndex);
        }
      }
      openedGroups.value = [...nextOpened];
    },
  );

  watch(
    () => props.errorsRaw,
    () => {
      navigationErrors.value = getRulesErrors(props.form.rules);
    },
    { immediate: true },
  );

  onMounted(() => {
    if (props.form.rules[0]) {
      props.form.rules[0].active = true;
    }
    openedGroups.value = props.form.rules.map((_, ruleIndex) => ruleIndex);
  });

  return {
    t,
    activeItem,
    navigationHidden,
    nameDescription,
    isGeneralError,
    navigationErrors,
    navigationErrorsRulesIndex,
    navigationErrorsPathsIndex,
    openedGroups,
    getNavigationIndexKey,
    handleMoveRule,
    toggleGroup,
    openNavigation,
    selectFlowSettings,
    setRule,
    setPath,
  };
};
